import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CheckCircle,
  ArrowRight,
  Crown,
  Check,
  Zap,
  Gift,
  Mail,
  HelpCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { useAppSelector, useAppDispatch } from "@/hooks/redux";
import { checkAuthStatus } from "@/store/slices/authSlice";
import authService from "@/services/authService";
import plansService, { type Plan } from "@/services/plansService";
import { captureFatalException } from "@/config/sentry";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const SubscriptionSuccessPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [isContactSupportOpen, setIsContactSupportOpen] = React.useState(false);

  const [userPlan, setUserPlan] = useState<Plan | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAppSelector((state) => state.auth);

  // Fetch user plan data with useCallback to prevent infinite re-renders
  const fetchUserData = useCallback(async () => {
    if (!user?.subscriptionPlan) return;

    try {
      setLoading(true);

      // Fetch user plan details
      const plans = await plansService.getPlans();
      const currentPlan = plans.find(
        (plan) => plan.name === user.subscriptionPlan,
      );
      if (currentPlan) {
        setUserPlan(currentPlan);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      toast.error("Failed to load subscription details");
    } finally {
      setLoading(false);
    }
  }, [user?.subscriptionPlan]);

  // Verify and update user data when component mounts
  useEffect(() => {
    const sessionId = searchParams.get("session_id") || "";

    // ✅ Utiliser sessionStorage pour persister même avec React.StrictMode
    const verificationKey = `stripe_verified_${sessionId}`;
    const hasAlreadyVerified = sessionStorage.getItem(verificationKey);

    // ✅ Si déjà vérifié (même après démontage/remontage en StrictMode), skip
    if (hasAlreadyVerified) {
      return;
    }

    // Pas de session_id, ne rien faire
    if (!sessionId) {
      return;
    }

    // ✅ Marquer IMMÉDIATEMENT comme vérifié dans sessionStorage
    sessionStorage.setItem(verificationKey, "true");

    const verifySubscription = async () => {
      try {
        // Verify with backend and update user data
        const verificationResult = await authService.verifyStripeSession({
          sessionId,
        });

        if (verificationResult.success) {
          // Refresh auth status to get updated user data
          await dispatch(checkAuthStatus());
          toast.success(t("subscription.success.verified"));
        } else {
          // ✅ ERREUR FATALE : Vérification échouée après paiement
          captureFatalException(
            new Error("Stripe session verification returned success=false"),
            {
              operation: "stripe_session_verification",
              sessionId,
              userId: user?.id,
              userEmail: user?.email,
              requires_immediate_attention: true,
              impact: "user_paid_but_subscription_not_activated",
              financial_impact: true,
            },
          );
          toast.error(t("subscription.success.verificationError"));
        }
      } catch (error) {
        // ✅ ERREUR FATALE : Utilisateur a payé mais abonnement non activé
        captureFatalException(error as Error, {
          operation: "stripe_session_verification",
          sessionId,
          userId: user?.id,
          userEmail: user?.email,
          requires_immediate_attention: true,
          impact: "user_paid_but_subscription_not_activated",
          financial_impact: true,
        });

        console.error("Error verifying subscription:", error);
        toast.error(t("subscription.success.verificationError"));
      }
    };

    verifySubscription();
    // ✅ Dépendances minimales - s'exécute une seule fois au montage
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch user data when user data is available
  useEffect(() => {
    if (user) {
      fetchUserData();
    }
  }, [user, fetchUserData]);

  const handleContinue = () => {
    navigate("/dashboard");
  };

  const getPlanIcon = (planName: string) => {
    switch (planName?.toLowerCase()) {
      case "starter":
        return <Zap className="w-6 h-6 " />;
      case "pro":
        return <Crown className="w-6 h-6 " />;
      case "maxi":
        return <Gift className="w-6 h-6 " />;
      default:
        return <Crown className="w-6 h-6 " />;
    }
  };

  const handleContactSupport = () => {
    const email = import.meta.env.VITE_SUPPORT_EMAIL;
    const subject = encodeURIComponent(
      `Demande de Support - ${import.meta.env.VITE_APP_NAME}`,
    );
    const currentDate = new Date().toLocaleDateString("fr-FR");
    const currentTime = new Date().toLocaleTimeString("fr-FR");

    const body = encodeURIComponent(
      `Bonjour l'équipe Support,

J'ai besoin d'assistance concernant :
[Veuillez décrire votre problème ou question ici]


TYPE DE PROBLÈME :
□ Problème de connexion / authentification
□ Problème avec un chatbot IA
□ Problème de facturation / abonnement
□ Problème technique (bugs, erreurs)
□ Question sur l'utilisation
□ Demande de fonctionnalité
□ Autre : _______________


DÉTAILS TECHNIQUES :
- Navigateur utilisé : ${
        navigator.userAgent.includes("Chrome")
          ? "Chrome"
          : navigator.userAgent.includes("Firefox")
            ? "Firefox"
            : navigator.userAgent.includes("Safari")
              ? "Safari"
              : navigator.userAgent.includes("Edge")
                ? "Edge"
                : "Autre"
      }
- Système d'exploitation : ${navigator.platform || "Non déterminé"}
- Date et heure : ${currentDate} à ${currentTime}
- URL de la page concernée : ${window.location.href}


INFORMATIONS UTILISATEUR :
- Email : ${user?.email || "Non renseigné"}
- Nom complet : ${user?.firstName || ""} ${user?.lastName || ""}
- Plan d'abonnement : ${user?.subscriptionPlan || "Gratuit"}
- Statut d'abonnement : ${user?.subscriptionStatus || "Non défini"}
- Email vérifié : ${user?.isEmailVerified ? "Oui" : "Non"}


ÉTAPES POUR REPRODUIRE LE PROBLÈME :
1. 
2. 
3. 

RÉSULTAT ATTENDU :


RÉSULTAT OBTENU :


Merci pour votre aide et votre support.

Cordialement,
${user?.firstName || ""} ${user?.lastName || ""}`,
    );

    // Create mailto link
    const mailtoLink = `mailto:${email}?subject=${subject}&body=${body}`;

    // Open email client
    window.location.href = mailtoLink;
  };

  if (loading) {
    return (
      <div className="min-h-screen  flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-slate-700 dark:text-slate-300">
            {t("subscription.success.loading")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen  dark:from-green-950 dark:to-emerald-950 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        {/* Success Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 dark:bg-green-900 rounded-full mb-6">
            <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-green-900 dark:text-green-100 mb-4">
            {t("subscription.success.title")}
          </h1>
          <p className="text-xl text-green-700 dark:text-green-300 max-w-2xl mx-auto">
            {t("subscription.success.subtitle", {
              planName: userPlan?.name,
            })}
          </p>
        </div>

        {/* Plan Details */}
        {userPlan && (
          <Card className="mb-8 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border-slate-200 dark:border-slate-800">
            <CardHeader className="text-center">
              <div className="flex items-center justify-center mb-4">
                {getPlanIcon(userPlan.name)}
                <CardTitle className="text-2xl font-bold ml-3  ">
                  {userPlan.name} {t("subscription.success.plan")}
                </CardTitle>
              </div>
              <div className="flex items-center justify-center">
                <span className="text-3xl font-bold  ">
                  {userPlan.currency}
                  {userPlan.price}
                </span>
                <span className="text-lg text-gray-600 dark:text-gray-400 ml-1">
                  {t("subscription.success.perMonth")}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Plan Features */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
                    {t("subscription.success.benefits.features.title")}
                  </h3>
                  <div className="space-y-3">
                    {userPlan.features.map((feature, index) => (
                      <div key={index} className="flex items-start">
                        <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Next Steps */}
        <Card className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border-slate-200 dark:border-slate-800">
          <CardHeader>
            <CardTitle className="text-center text-gray-900 dark:text-gray-100">
              {t("subscription.success.nextSteps.title")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {t("subscription.success.nextSteps.description")}
              </p>
              <Button
                onClick={handleContinue}
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-3"
              >
                {t("subscription.success.continueToDashboard")}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Contact Support Dialog */}
        <div className="text-center mt-8">
          <Dialog
            open={isContactSupportOpen}
            onOpenChange={setIsContactSupportOpen}
          >
            <DialogTrigger asChild>
              <Button variant="outline">
                <HelpCircle className="h-4 w-4 mr-2" />
                {t("dashboard.contactSupport")}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <HelpCircle className="h-5 w-5" />
                  {t("dashboard.contactSupport")}
                </DialogTitle>
                <DialogDescription>
                  {t("dashboard.contactSupportDescription")}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex flex-col space-y-3">
                  <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/50">
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium">Email Support</p>
                        <p className="text-sm text-muted-foreground">
                          {import.meta.env.VITE_SUPPORT_EMAIL}
                        </p>
                      </div>
                    </div>
                  </div>

                  <Button onClick={handleContactSupport} className="w-full">
                    <Mail className="h-4 w-4 mr-2" />
                    {t("dashboard.openEmailApp")}
                  </Button>

                  <p className="text-xs text-muted-foreground text-center">
                    {t("dashboard.emailAppNote")}
                  </p>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;
